/**
 * A message's body made safe to draw in the reading pane (piece 32): the same
 * allowlist rule as the editor's (decision 0360) — a tag is kept only when its
 * name is allowed, every attribute is rebuilt from what is allowed, and every
 * other `<` is escaped into text. 🔨 Remote images are blocked by default and
 * counted, so the pane can offer to show them; an inline image named by `cid:`
 * is drawn from the message's own attachment. 🔒 A `style` keeps only the email
 * editor's bounded declarations and a `font` element its face, size and colour
 * (T3423; decision 1537); every other declaration, every URL and every
 * `expression(` or `@import` is dropped.
 */

import { EMAIL_ALLOWANCE, emailStyleOf } from '../modals/emailBody';
import { SAFE_HREF, escapeAttribute, escapeHtml, escapeText } from '../controls/richText';

/** The tags a mail body keeps, each with the attributes it may carry. */
export const MAIL_TAGS: Readonly<Record<string, readonly string[]>> = {
  p: ['style'], br: [], div: ['style'], span: ['style'], hr: [], pre: [], code: [], small: [], sup: [], sub: [], center: [],
  // 🔒 T3423: the email consumers' `font` element, its face, size and colour each checked as the editor checks them.
  font: ['face', 'size', 'color'],
  b: [], strong: [], i: [], em: [], u: [], s: [], strike: [],
  ul: [], ol: [], li: [], dl: [], dt: [], dd: [],
  blockquote: [], h1: [], h2: [], h3: [], h4: [], h5: [], h6: [],
  table: [], thead: [], tbody: [], tfoot: [], tr: [], td: ['colspan', 'rowspan', 'style'], th: ['colspan', 'rowspan', 'style'], caption: [],
  a: ['href'],
  img: ['src', 'alt', 'width', 'height'],
};

/** A private-use character that stands in for a kept tag while the rest is escaped; stripped from the input first. */
const MARK = '\uE000';

const CODE_ELEMENTS = /<(script|style|iframe|object|embed|noscript|template|head|title|xml)\b[\s\S]*?<\/\1\s*>/gi;
/** A tag with attributes quoted either way or not at all; a tag that fails this is escaped as text. */
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*(\/?)>/g;
const ATTRIBUTE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

export interface MailRenderOptions {
  /** The address an inline image's `cid:` is drawn from, or null where the message holds no such part. */
  inlineUrl: (contentId: string) => string | null;
  showRemoteImages: boolean;
}

export interface RenderedMailBody {
  html: string;
  /** How many remote images were held back, which is what the Show images control offers. */
  blockedRemoteImages: number;
}

export function sanitizeMailHtml(html: string, options: MailRenderOptions): RenderedMailBody {
  let blocked = 0;
  if (!html) return { html: '', blockedRemoteImages: 0 };
  const body = /<body\b[^>]*>([\s\S]*?)(?:<\/body\s*>|$)/i.exec(html)?.[1] ?? html;
  const source = body.replace(/\uE000/g, '').replace(CODE_ELEMENTS, '').replace(/<!--[\s\S]*?-->/g, '').replace(/<!DOCTYPE[^>]*>/gi, '');
  const kept: string[] = [];
  const held = source.replace(TAG, (_whole, closing: string, rawName: string, rawAttributes: string) => {
    const name = rawName.toLowerCase();
    const allowed = MAIL_TAGS[name];
    if (!allowed) return '';
    if (closing) {
      kept.push(`</${name}>`);
      return `${MARK}${kept.length - 1}${MARK}`;
    }
    const attributes = new Map<string, string>();
    for (const match of rawAttributes.matchAll(ATTRIBUTE)) {
      const attribute = match[1]!.toLowerCase();
      if (!allowed.includes(attribute)) continue;
      const raw = (match[2] ?? match[3] ?? match[4] ?? '').trim();
      // 🔒 T3423: a style keeps only the editor's own five declarations and a `font` attribute only a value the editor writes, so no URL, expression or import rides through.
      const value = attribute === 'style' ? emailStyleOf(raw) : name === 'font' ? (EMAIL_ALLOWANCE.attribute?.(attribute, raw) ?? null) : raw;
      if (value === null || value === '') continue;
      attributes.set(attribute, value);
    }
    if (name === 'img') {
      const src = attributes.get('src') ?? '';
      const alt = attributes.get('alt') ?? '';
      if (/^cid:/i.test(src)) {
        const url = options.inlineUrl(src.slice(4).replace(/^<|>$/g, ''));
        if (!url) return '';
        attributes.set('src', url);
      } else if (/^https?:\/\//i.test(src)) {
        if (!options.showRemoteImages) {
          blocked += 1;
          kept.push(alt ? `<span data-remote-image="blocked">[${escapeText(alt)}]</span>` : '<span data-remote-image="blocked"></span>');
          return `${MARK}${kept.length - 1}${MARK}`;
        }
      } else return '';
    }
    if (name === 'a') {
      const href = attributes.get('href') ?? '';
      if (!SAFE_HREF.test(href)) attributes.delete('href');
    }
    let out = `<${name}`;
    for (const [attribute, value] of attributes) {
      if ((attribute === 'width' || attribute === 'height' || attribute === 'colspan' || attribute === 'rowspan') && !/^\d{1,4}%?$/.test(value)) continue;
      out += ` ${attribute}="${escapeAttribute(value)}"`;
    }
    if (name === 'a' && attributes.has('href')) out += ' rel="noopener noreferrer" target="_blank"';
    kept.push(`${out}>`);
    return `${MARK}${kept.length - 1}${MARK}`;
  });
  const safe = escapeText(held).replace(/\uE000(\d+)\uE000/g, (_whole, index: string) => kept[Number(index)] ?? '');
  return { html: safe, blockedRemoteImages: blocked };
}

const ENTITIES: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/** The words of a mail body with its markup gone: what search indexes, what Body as Note stores and what the list's first line reads. */
export function mailPlainText(html: string): string {
  return html
    .replace(CODE_ELEMENTS, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|table)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(#\d+|#39|[a-z]+);/gi, (whole, name: string) => (name.startsWith('#') && name !== '#39' ? String.fromCharCode(Number(name.slice(1))) : ENTITIES[name.toLowerCase()] ?? whole))
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n+/g, '\n')
    .trim();
}

/** Plain text as a mail body, escaped with its paragraphs kept: a text/plain message drawn as HTML. */
export function mailHtmlFromText(text: string): string {
  const escaped = escapeHtml(text.replace(/\r\n/g, '\n').trim());
  return escaped ? `<p>${escaped.replace(/\n[ \t]*\n\s*/g, '</p><p>').replace(/\n/g, '<br>')}</p>` : '';
}

/** The list row's first line: the body's opening words, on one line. */
export function firstLine(text: string, length = 140): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= length ? flat : `${flat.slice(0, length - 1).trimEnd()}…`;
}

/**
 * 🔒 The stored preview a list row draws (decision 1478; T609): the body's first plain words, cut to one line. Every
 * writer of a body writes it beside the body, so the list reads this and never the body.
 */
export function messageSnippet(html: string): string {
  return firstLine(mailPlainText(html));
}

/** Every link in a body with the text it shows, which the link-mismatch signal reads. */
export function linksOf(html: string): { text: string; href: string }[] {
  const out: { text: string; href: string }[] = [];
  for (const m of html.matchAll(/<a\b[^>]*?href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi)) {
    out.push({ href: (m[1] ?? m[2] ?? m[3] ?? '').trim(), text: mailPlainText(m[4] ?? '') });
  }
  return out;
}
